import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "~/lib/utils"

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      // FRONT-1818: forwarded to Radix, not merely stamped as a data
      // attribute. Radix drives its roving focus off its OWN `orientation`
      // prop, so a vertical list left at the default answered to Left/Right
      // arrows while looking like a column that should answer to Up/Down.
      // Nothing caught it because the Settings rail is the first vertical
      // caller — every other caller is horizontal, where the two agree.
      orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-horizontal:flex-col",
        className
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  // FRONT-2376: keyed off the list's OWN `data-orientation` (Radix sets it
  // directly on the list element via the roving-focus-group it renders as,
  // scoped to its nearest Tabs.Root), not `group-data-*/tabs:` — that variant
  // matches ANY ancestor carrying the `group/tabs` class and `data-orientation`,
  // so a horizontal Tabs nested inside a vertical one (Org Settings' inner
  // tabs inside the System Settings modal's vertical rail) inherited the
  // outer Tabs' vertical layout instead of its own.
  //
  // FRONT-2874: one phone-width rule for every horizontal bar. When the tabs do
  // not fit, they wrap onto a second row, left-aligned, inside the row that
  // holds the bar (`max-w-full`); nothing scrolls sideways and nothing sticks
  // out past the edge, so every tab stays visible. On a wide screen a bar that
  // fits never wraps, so desktop is unchanged. The height grows with the rows
  // (`min-h-8` keeps the old one-row floor). Pages must not add their own
  // overflow handling on top — the tab-bar sweep's rule D fails if one does.
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground data-horizontal:min-h-8 data-horizontal:max-w-full data-horizontal:flex-wrap data-horizontal:justify-start data-vertical:h-fit data-vertical:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        // FRONT-2464: the SAME look as the shared `Segmented` pill group
        // (components/ui/segmented.tsx) — bordered track, bordered card-white
        // active pill — so a page's tab bar and the filter under it read as
        // one control language, not two. Height comes from the triggers.
        default: "gap-1 rounded-[12px] border border-border bg-muted p-1",
        line: "gap-1 bg-transparent",
      },
      // FRONT-2485: `sm` is the secondary row under a page's primary tabs
      // (Sketches' owner filter under the Knowledge tabs, Reflections' To
      // review / History under Memory's) — same look, smaller, so a stacked
      // pair reads as one family with a clear primary. Mirrors `Segmented`'s
      // own `size="sm"`; the triggers read it off the list's `data-size`.
      size: {
        default: "",
        sm: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function TabsList({
  className,
  variant = "default",
  size = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      data-size={size}
      className={cn(tabsListVariants({ variant, size }), className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-transparent px-3.5 py-1.5 text-sm font-medium whitespace-nowrap text-foreground/60 transition-[color,background-color,border-color,box-shadow] data-vertical:w-full data-vertical:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 dark:text-muted-foreground dark:hover:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        // FRONT-2874: below `sm` each tab is as wide as its own label, so a
        // wrapped bar packs its rows instead of stretching tabs to fill each
        // row. From `sm` up `flex-1` stays, so desktop sizing is unchanged.
        // Horizontal only: the vertical rail is a column. The transition above
        // names its properties: `transition-all` would also animate the
        // flex-grow change when a window is dragged across `sm`.
        "max-sm:data-horizontal:flex-none",
        // FRONT-2485: the `sm` list — `Segmented`'s own sm padding and text.
        "group-data-[size=sm]/tabs-list:px-3 group-data-[size=sm]/tabs-list:py-1 group-data-[size=sm]/tabs-list:text-xs",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent",
        // FRONT-2464: the active pill matches `Segmented`'s. Plain classes (not
        // `group-data-*`) on purpose, so a caller's own className still wins
        // through twMerge — the settings rail and the Agents chips restyle it.
        "data-active:border-border data-active:bg-card data-active:font-semibold data-active:text-foreground dark:data-active:text-foreground group-data-[variant=line]/tabs-list:data-active:border-transparent",
        // Same fix as above: the trigger's own `data-orientation` (also set by
        // the roving-focus-group item, scoped to its nearest Tabs.Root), not
        // an ancestor's.
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity data-horizontal:after:inset-x-0 data-horizontal:after:bottom-[-5px] data-horizontal:after:h-0.5 data-vertical:after:inset-y-0 data-vertical:after:-right-1 data-vertical:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 text-sm outline-none", className)}
      {...props}
    />
  )
}

/**
 * FRONT-2425: a number inside a tab, styled as a notification — a small solid
 * pill in the primary blue, the same hue the sidebar uses for the numbers
 * waiting on you. Only for a count the person can act on to bring down (a
 * review queue, a recycle bin to empty); a plain inventory total ("Files ·
 * 251") does not belong in a tab at all. Renders nothing at zero or unknown,
 * caps at 99+ like the sidebar and the bell, and states the exact count to a
 * screen reader through `label`, since a bare digit after a tab name explains
 * nothing.
 */
function TabCount({
  value,
  label,
  className,
}: {
  value: number | null | undefined
  /** What the number is, read out in full ("92 may be out of date"). */
  label: string
  className?: string
}) {
  if (!value || value < 0) return null
  return (
    <span
      data-slot="tab-count"
      className={cn(
        "inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-[5px] text-[10.5px] leading-none font-semibold tracking-[0.01em] text-primary-foreground tabular-nums shadow-[0_1px_2px_rgb(0_0_0/0.12)] group-data-[size=sm]/tabs-list:h-4 group-data-[size=sm]/tabs-list:min-w-4 group-data-[size=sm]/tabs-list:text-[10px]",
        className
      )}
    >
      <span aria-hidden="true">{value > 99 ? "99+" : value}</span>
      <span className="sr-only">{label}</span>
    </span>
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, TabCount, tabsListVariants }

/**
 * FRONT-2485: the one-panel wiring for a tab bar whose tabs re-render ONE
 * body rather than reveal separate `TabsContent`s (a page's view switch —
 * Knowledge, Memory, Chats, Inbox, Notifications). Radix stamps
 * `aria-controls` at a per-value content id on every trigger, so without a
 * panel every tab points at an element that is not in the DOM. Spread
 * `trigger(value)` on each `TabsTrigger` (caller props win over Radix's) and
 * `panel` on the element that holds the body: one `role="tabpanel"` whose
 * `aria-labelledby` follows the active tab — the same pattern the gallery
 * controls and the Agents page wire by hand.
 */
function useTabPanel(active: string) {
  const base = React.useId()
  const panelId = `${base}-panel`
  return {
    trigger: (value: string) => ({ id: `${base}-tab-${value}`, "aria-controls": panelId }),
    panel: { id: panelId, role: "tabpanel" as const, "aria-labelledby": `${base}-tab-${active}` },
  }
}

export { useTabPanel }
